import coding from './shipment-location-codes.v7.9.json'

// RC_IITP.IS_V7.9 sections 8-67 to 8-70: a bill of lading (pattern 8) names its origin/destination by the
// codes of appendix 9-4 (countries) and 9-5 (cities). The backend validates against the same file
// (MegaPay.FiscalMemory/Rules/shipment-location-codes.v7.9.json).

export type ShipmentLocationKind = 'country' | 'city'

export type ShipmentLocation = {
  code: string
  name: string
  alias?: string
}

export const SHIPMENT_LOCATION_SOURCE = coding.source

// Iran is 033 in the official table, not the ISO 3166 code 364.
export const IRAN_SHIPMENT_COUNTRY_CODE = '033'

export const SHIPMENT_COUNTRIES: ShipmentLocation[] = coding.countries.map(([code, name, alias]) => ({
  code,
  name,
  alias,
}))

export const SHIPMENT_CITIES: ShipmentLocation[] = coding.cities.map(([code, name]) => ({code, name}))

const countriesByCode = new Map(SHIPMENT_COUNTRIES.map((location) => [location.code, location]))
const citiesByCode = new Map(SHIPMENT_CITIES.map((location) => [location.code, location]))

export const findShipmentLocation = (
  kind: ShipmentLocationKind,
  code: string | null | undefined
): ShipmentLocation | undefined =>
  (kind === 'country' ? countriesByCode : citiesByCode).get((code || '').trim())

export const isShipmentCountryCode = (code: string | null | undefined) =>
  findShipmentLocation('country', code) !== undefined

export const isShipmentCityCode = (code: string | null | undefined) =>
  findShipmentLocation('city', code) !== undefined

export const isShipmentCityRequired = (countryCode: string | null | undefined) =>
  (countryCode || '').trim() === IRAN_SHIPMENT_COUNTRY_CODE

/** A city is valid when it is an official code, or empty for a country other than Iran. */
export const isShipmentLocationValid = (countryCode: string, cityCode: string) => {
  if (!isShipmentCountryCode(countryCode)) return false
  if (!cityCode.trim()) return !isShipmentCityRequired(countryCode)
  return isShipmentCityCode(cityCode)
}

/** «ایران (جمهوری اسلامی) — 033»; an unknown code is shown as it is. */
export const formatShipmentLocation = (
  kind: ShipmentLocationKind,
  code: string | null | undefined
): string | undefined => {
  const value = (code || '').trim()
  if (!value) return undefined
  const location = findShipmentLocation(kind, value)
  return location ? `${location.name} — ${location.code}` : value
}

const persianDigits = '۰۱۲۳۴۵۶۷۸۹'
const arabicDigits = '٠١٢٣٤٥٦٧٨٩'

export const normalizeShipmentLocationSearch = (value: string) =>
  value
    .normalize('NFKC')
    .replace(/[۰-۹]/g, (digit) => String(persianDigits.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(arabicDigits.indexOf(digit)))
    .replace(/[يى]/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/ة/g, 'ه')
    .replace(/[آأإ]/g, 'ا')
    .toLowerCase()
    .replace(/[\s‌‏‎()[\]{}،,.\-_/]/g, '')

type SearchEntry = {location: ShipmentLocation; name: string; alias: string}

const toEntries = (locations: ShipmentLocation[]): SearchEntry[] =>
  locations
    .map((location) => ({
      location,
      name: normalizeShipmentLocationSearch(location.name),
      alias: normalizeShipmentLocationSearch(location.alias || ''),
    }))
    .sort((left, right) => left.location.name.localeCompare(right.location.name, 'fa'))

const countryEntries = toEntries(SHIPMENT_COUNTRIES)
const cityEntries = toEntries(SHIPMENT_CITIES)

const rank = (entry: SearchEntry, query: string) => {
  const code = entry.location.code
  if (code === query || code.replace(/^0+/, '') === query.replace(/^0+/, '')) return 0
  if (entry.name.startsWith(query)) return 1
  if (entry.alias.startsWith(query)) return 2
  if (entry.name.includes(query)) return 3
  if (entry.alias.includes(query)) return 4
  if (code.startsWith(query)) return 5
  return -1
}

/**
 * Matches by Persian or English name or by code, best matches first. Without a query Iran leads the
 * country list, since most bills of lading start or end there.
 */
export const searchShipmentLocations = (
  kind: ShipmentLocationKind,
  query: string,
  limit: number
): {items: ShipmentLocation[]; total: number} => {
  const entries = kind === 'country' ? countryEntries : cityEntries
  const normalized = normalizeShipmentLocationSearch(query)

  if (!normalized) {
    const all = entries.map((entry) => entry.location)
    const ordered =
      kind === 'country'
        ? [
            ...all.filter((location) => location.code === IRAN_SHIPMENT_COUNTRY_CODE),
            ...all.filter((location) => location.code !== IRAN_SHIPMENT_COUNTRY_CODE),
          ]
        : all
    return {items: ordered.slice(0, limit), total: ordered.length}
  }

  const matches = entries
    .map((entry) => ({entry, score: rank(entry, normalized)}))
    .filter((match) => match.score >= 0)
    .sort((left, right) => left.score - right.score)
    .map((match) => match.entry.location)

  return {items: matches.slice(0, limit), total: matches.length}
}
